import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import url from '../../utils/UrlHelper';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/new/UI/ArrowLeft.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/new/UI/ArrowRight.svg';

export interface StocksTab {
    id: string;
    label: string;
    urlTemplate: string;
}

interface StocksChannelTabsProps {
    tabs: StocksTab[];
    selected: string;
    onAddWarehouse?: () => void;
    onReorder?: (orderedIds: string[]) => void;
}

const TOUCH_LONG_PRESS_MS = 350;
const TOUCH_MOVE_TOLERANCE = 10;

const StocksChannelTabs: React.FC<StocksChannelTabsProps> = ({ tabs, selected, onAddWarehouse, onReorder }) => {
    const { sellerId } = useAppSelector(({ sellers }) => ({
        sellerId: sellers.currentSeller.info.data?.id,
    }));
    const navigate = useNavigate();
    const currentIndex = tabs.findIndex(t => t.id === selected);

    const dragTabId = useRef<string | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);

    const reorder = (fromId: string, targetId: string) => {
        if (fromId === targetId || !onReorder) return;
        const fromIdx = tabs.findIndex(t => t.id === fromId);
        const toIdx = tabs.findIndex(t => t.id === targetId);
        if (fromIdx === -1 || toIdx === -1) return;
        const next = [...tabs];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        onReorder(next.map(t => t.id));
    };

    const handleTabDragStart = (e: React.DragEvent, tabId: string) => {
        dragTabId.current = tabId;
        e.dataTransfer.effectAllowed = 'move';
        // Safari и Firefox не начинают drag без данных в dataTransfer
        e.dataTransfer.setData('text/plain', tabId);
    };
    const handleTabDragOver = (e: React.DragEvent, tabId: string) => {
        if (tabId === dragTabId.current) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverId(tabId);
    };
    const handleTabDrop = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        const fromId = dragTabId.current;
        dragTabId.current = null;
        setDragOverId(null);
        if (fromId) reorder(fromId, targetId);
    };
    const handleTabDragEnd = () => {
        dragTabId.current = null;
        setDragOverId(null);
    };

    // Сенсорные экраны: нативный HTML5 drag-and-drop там ненадёжен, поэтому перетаскивание по долгому нажатию
    const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
    const touchRef = useRef<{ id: string; x: number; y: number; timer: number | null; active: boolean } | null>(null);
    const [touchDragId, setTouchDragId] = useState<string | null>(null);
    const [ghostPos, setGhostPos] = useState<{ x: number; y: number } | null>(null);
    const reorderRef = useRef(reorder);
    reorderRef.current = reorder;

    const handleTouchStart = (e: React.TouchEvent, tabId: string) => {
        if (!onReorder) return;
        const touch = e.touches[0];
        const state = { id: tabId, x: touch.clientX, y: touch.clientY, timer: null as number | null, active: false };
        state.timer = window.setTimeout(() => {
            state.active = true;
            setTouchDragId(tabId);
            setGhostPos({ x: state.x, y: state.y });
            navigator.vibrate?.(10);
        }, TOUCH_LONG_PRESS_MS);
        touchRef.current = state;
    };

    useEffect(() => {
        const reset = () => {
            const state = touchRef.current;
            if (state?.timer) window.clearTimeout(state.timer);
            touchRef.current = null;
            setTouchDragId(null);
            setDragOverId(null);
            setGhostPos(null);
        };
        const onMove = (e: TouchEvent) => {
            const state = touchRef.current;
            if (!state) return;
            const touch = e.touches[0];
            if (!state.active) {
                if (Math.hypot(touch.clientX - state.x, touch.clientY - state.y) > TOUCH_MOVE_TOLERANCE) reset();
                return;
            }
            e.preventDefault();
            setGhostPos({ x: touch.clientX, y: touch.clientY });
            const overId = document.elementFromPoint(touch.clientX, touch.clientY)?.closest<HTMLElement>('[data-tab-id]')?.dataset.tabId;
            setDragOverId(overId && overId !== state.id ? overId : null);
        };
        const onEnd = (e: TouchEvent) => {
            const state = touchRef.current;
            if (!state) return;
            if (state.active) {
                e.preventDefault();
                const touch = e.changedTouches[0];
                const overId = e.type === 'touchend'
                    ? document.elementFromPoint(touch.clientX, touch.clientY)?.closest<HTMLElement>('[data-tab-id]')?.dataset.tabId
                    : undefined;
                if (overId) reorderRef.current(state.id, overId);
            }
            reset();
        };
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onEnd, { passive: false });
        document.addEventListener('touchcancel', onEnd, { passive: false });
        return () => {
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onEnd);
            document.removeEventListener('touchcancel', onEnd);
        };
    }, []);

    const tabActiveStyle = {
        borderTop: '1px solid var(--tab-active-border)',
        borderRight: '1px solid var(--tab-active-border)',
        borderBottom: '2px solid #f4a621',
        borderLeft: '1px solid var(--tab-active-border)',
        background: 'var(--tab-active-bg)',
        color: 'var(--tab-active-color)',
    };
    const tabInactiveStyle = {
        borderTop: '1px solid var(--tab-inactive-border)',
        borderRight: '1px solid var(--tab-inactive-border)',
        borderBottom: '1px solid var(--tab-inactive-border)',
        borderLeft: '1px solid var(--tab-inactive-border)',
        background: 'var(--tab-inactive-bg)',
        color: 'var(--tab-inactive-color)',
    };
    const tabHoverBg = 'var(--tab-hover-bg)';

    const goPrev = () => {
        const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        navigate(url(tabs[prevIndex].urlTemplate, sellerId));
    };
    const goNext = () => {
        const nextIndex = (currentIndex + 1) % tabs.length;
        navigate(url(tabs[nextIndex].urlTemplate, sellerId));
    };

    return (
        <div className="flex items-center min-w-0">
            {/* Mobile: horizontal scroll tabs (no arrows) */}
            <div
                className="md:hidden flex items-center overflow-x-auto"
                style={{ height: 34, scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {tabs.map((tab, index) => {
                    const isActive = tab.id === selected;
                    return (
                        <Link
                            key={tab.id}
                            to={url(tab.urlTemplate, sellerId)}
                            data-tab-id={tab.id}
                            draggable={false}
                            onTouchStart={onReorder ? (e) => handleTouchStart(e, tab.id) : undefined}
                            onContextMenu={onReorder ? (e) => e.preventDefault() : undefined}
                            style={{
                                height: 34,
                                marginLeft: index === 0 ? 0 : -1,
                                zIndex: dragOverId === tab.id ? tabs.length + 5 : isActive ? tabs.length + 1 : tabs.length - index,
                                WebkitTouchCallout: 'none',
                                WebkitUserSelect: 'none',
                                userSelect: 'none',
                                opacity: touchDragId === tab.id ? 0.45 : 1,
                                outline: dragOverId === tab.id ? '2px solid #f4a621' : undefined,
                                outlineOffset: dragOverId === tab.id ? '-2px' : undefined,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 10px',
                                boxSizing: 'border-box',
                                flexShrink: 0,
                                borderRadius: '8px 8px 0 0',
                                fontSize: 12,
                                fontWeight: 400,
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                ...(isActive ? tabActiveStyle : tabInactiveStyle),
                                ...(isActive ? {} : { borderLeft: index === 0 ? tabInactiveStyle.borderTop : 'none' }),
                            }}
                        >
                            {tab.label}
                        </Link>
                    );
                })}
            </div>

            {/* Desktop: arrows + tabs */}
            <div className="hidden md:flex items-center gap-[4px]">
                {/* Arrows */}
                <div className="flex items-center gap-[4px] mx-[7px]">
                    <button
                        onClick={goPrev}
                        title="Предыдущая вкладка"
                        className="w-[20px] h-[20px] flex items-center justify-center rounded-full text-slate-400 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 hover:text-c-blue dark:hover:text-blue-300 transition-colors"
                    >
                        <ArrowLeftIcon width={14} height={14} style={{ fill: 'currentColor' }} />
                    </button>
                    <button
                        onClick={goNext}
                        title="Следующая вкладка"
                        className="w-[20px] h-[20px] flex items-center justify-center rounded-full text-slate-400 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 hover:text-c-blue dark:hover:text-blue-300 transition-colors"
                    >
                        <ArrowRightIcon width={14} height={14} style={{ fill: 'currentColor' }} />
                    </button>
                </div>

                {/* Tabs row */}
                <div className="flex items-center" style={{ height: 34 }}>
                    {tabs.map((tab, index) => {
                        const isActive = tab.id === selected;
                        return (
                            <Link
                                key={tab.id}
                                to={url(tab.urlTemplate, sellerId)}
                                title={tab.label}
                                data-tab-id={tab.id}
                                draggable={!!onReorder && !isTouchDevice}
                                onTouchStart={onReorder ? (e) => handleTouchStart(e, tab.id) : undefined}
                                onContextMenu={onReorder && isTouchDevice ? (e) => e.preventDefault() : undefined}
                                onDragStart={onReorder ? (e) => handleTabDragStart(e, tab.id) : undefined}
                                onDragOver={onReorder ? (e) => handleTabDragOver(e, tab.id) : undefined}
                                onDragLeave={onReorder ? () => setDragOverId(null) : undefined}
                                onDrop={onReorder ? (e) => handleTabDrop(e, tab.id) : undefined}
                                onDragEnd={onReorder ? handleTabDragEnd : undefined}
                                style={{
                                    width: 88,
                                    height: 34,
                                    marginLeft: index === 0 ? 0 : -8,
                                    zIndex: dragOverId === tab.id ? tabs.length + 5 : isActive ? tabs.length + 1 : tabs.length - index,
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box',
                                    flexShrink: 0,
                                    borderRadius: '8px 8px 0 0',
                                    fontSize: 12,
                                    fontWeight: 400,
                                    textDecoration: 'none',
                                    cursor: onReorder ? 'grab' : 'pointer',
                                    userSelect: 'none',
                                    transition: 'background 0.12s, color 0.12s, opacity 0.12s',
                                    WebkitTouchCallout: 'none',
                                    WebkitUserSelect: 'none',
                                    opacity: dragTabId.current === tab.id || touchDragId === tab.id ? 0.45 : 1,
                                    outline: dragOverId === tab.id ? '2px solid #f4a621' : undefined,
                                    outlineOffset: dragOverId === tab.id ? '-2px' : undefined,
                                    ...(isActive ? tabActiveStyle : tabInactiveStyle),
                                    ...(!isActive && index > 0 ? { borderLeft: 'none' } : {}),
                                }}
                                onMouseEnter={(e) => {
                                    const el = e.currentTarget as HTMLElement;
                                    if (isActive) return;
                                    el.style.background = tabHoverBg;
                                    el.style.borderTop = '1px solid #f4a621';
                                    el.style.borderRight = '1px solid #f4a621';
                                    el.style.borderLeft = index > 0 ? 'none' : '1px solid #f4a621';
                                }}
                                onMouseLeave={(e) => {
                                    const el = e.currentTarget as HTMLElement;
                                    el.style.background = isActive ? tabActiveStyle.background : tabInactiveStyle.background;
                                    const style = isActive ? tabActiveStyle : tabInactiveStyle;
                                    el.style.borderTop = style.borderTop;
                                    el.style.borderRight = style.borderRight;
                                    el.style.borderLeft = (!isActive && index > 0) ? 'none' : style.borderLeft;
                                }}
                            >
                                <span className="truncate px-1 leading-none">{tab.label}</span>
                            </Link>
                        );
                    })}
                </div>

                {/* +Склад button */}
                {onAddWarehouse && (
                    <button
                        onClick={onAddWarehouse}
                        style={{ position: 'relative', zIndex: tabs.length + 10 }}
                        className="ml-1 h-[28px] px-[8px] flex items-center gap-[4px] text-[12px] font-medium border border-dashed border-slate-300 dark:border-gray-600 text-slate-500 dark:text-slate-400 rounded-[8px] hover:border-c-blue hover:text-c-blue dark:hover:text-blue-400 transition-colors shrink-0"
                    >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        Склад
                    </button>
                )}
            </div>

            {/* Touch-drag ghost: копия вкладки следует за пальцем */}
            {touchDragId && ghostPos && (() => {
                const dragTab = tabs.find(t => t.id === touchDragId);
                if (!dragTab) return null;
                return (
                    <div
                        aria-hidden
                        className="md:hidden"
                        style={{
                            position: 'fixed',
                            left: ghostPos.x,
                            top: ghostPos.y - 20,
                            transform: 'translate(-50%, -50%)',
                            zIndex: 999,
                            pointerEvents: 'none',
                            height: 34,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 10px',
                            boxSizing: 'border-box',
                            borderRadius: '8px 8px 0 0',
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                            ...tabActiveStyle,
                            borderBottom: '2px solid #f4a621',
                        }}
                    >
                        {dragTab.label}
                    </div>
                );
            })()}
        </div>
    );
};

export default StocksChannelTabs;
